import {
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	LoadingOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Flex, Space, Tooltip, Typography } from 'antd';
import { SortOrder } from 'antd/es/table/interface';
import Link from 'antd/es/typography/Link';

import { CopilotHistoryBatchItem } from '@/api/batch_process';
import FavouriteIcon from '@/components/common/FavouriteIcon';
import placeholderImage from '@/lib/assets/placeholder.jpg';
import prepareDate from '@/lib/helpers/prepareDate';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';

import Platforms from '../../creators/platforms';
import ThumbnailImage from './../ThumbnailImage';

export enum STATUSES {
	'Adding to queue...' = 0,
	'Processing...' = 1,
	'Analysis complete' = 2,
	'Error during analysis' = 3,
	'Error during analysis 2' = 4,
	'Error during analysis 3' = 5,
	'Video too long' = 6,
	'Video too short' = 7,
}

const columns = [
	{
		title: 'Status / Id',
		dataIndex: 'batch_id',
		key: 'batch_id',
		width: 60,
		// hidden: true,
		render: (_: any, record: CopilotHistoryBatchItem) => {
			const statusSet = [
				...new Set<number | null>([
					...record.videos.map((v) => v.status || null),
				]),
			];
			const allReady = statusSet?.every(
				(status) => status === STATUSES['Analysis complete'],
			);
			const anyError = statusSet?.some(
				(status) =>
					status === STATUSES['Error during analysis'] ||
					status === STATUSES['Error during analysis 2'] ||
					status === STATUSES['Error during analysis 3'] ||
					status === STATUSES['Video too long'] ||
					status === STATUSES['Video too short'],
			);
			const anyProcessing = statusSet?.some(
				(status) =>
					status === STATUSES['Processing...'] ||
					status === STATUSES['Adding to queue...'],
			);

			let IconComponent = null;

			if (allReady) {
				IconComponent = (
					<Tooltip title={'Analysis complete'}>
						<CheckCircleOutlined
							style={{
								color: '#58C622',
							}}
							// className={styles.indicatorIcon}
						/>
					</Tooltip>
				);
			} else if (anyError) {
				IconComponent = (
					<Tooltip
						title={'We experienced an issue analysing one or more videos.'}
					>
						<ExclamationCircleOutlined
							style={{
								color: 'orange',
							}}
							// className={styles.indicatorIcon}
						/>
					</Tooltip>
				);
			} else if (anyProcessing) {
				IconComponent = (
					<Tooltip title={'Processing...'}>
						<SyncOutlined
							spin
							style={{
								color: '#172b6f',
							}}
							// className={styles.indicatorIcon}
						/>
					</Tooltip>
				);
			} else {
				IconComponent = (
					<Tooltip title={'Adding to queue...'}>
						<SyncOutlined
							spin
							style={{
								color: '#172b6f',
							}}
							// className={styles.indicatorIcon}
						/>
					</Tooltip>
				);
			}

			return (
				<p>
					{IconComponent}
					<span style={{ marginLeft: 8 }}>
						{record.batch_id?.split('-')?.[0]}
					</span>
				</p>
			);
		},
	},
	{
		title: 'Videos',
		dataIndex: 'history_count',
		key: 'history_count',
		width: 60,
		// flex: true,
		// ellipsis: true,
		// hidden: true,
		render: (_: any, record: CopilotHistoryBatchItem) => {
			return <>{record?.history_count ?? '0'}</>;
		},
	},
	{
		title: 'Date/time created',
		dataIndex: 'created_at',
		key: 'created_at',
		sorter: true,
		sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
		width: 60,
		// flex: true,
		// ellipsis: true,
		// hidden: true,
		render: (_: any, record: CopilotHistoryBatchItem) => {
			return (
				<>
					{record?.created_at
						? prepareDate('' + record?.created_at)
						: 'no date'}
				</>
			);
		},
	},
	// {
	// 	title: 'Status / Title',
	// 	dataIndex: 'title',
	// 	key: 'title',
	// 	width: 200,
	// 	ellipsis: true,
	// 	render: (_: any, record: CopilotHistoryBatchItem) => {
	// 		const preparedTitle =
	// 			(record?.title || record?.description)?.length > 90
	// 				? (record?.title || record?.description).slice(0, 90) + '...'
	// 				: record?.title || record?.description;

	// 		let IconComponent = null;

	// 		switch (record?.status) {
	// 			case 0:
	// 			case undefined:
	// 				IconComponent = (
	// 					<Tooltip title={'Adding to queue...'}>
	// 						<LoadingOutlined
	// 							style={{ color: 'gray' }}
	// 							// className={styles.indicatorIcon}
	// 						/>
	// 					</Tooltip>
	// 				);
	// 				break;

	// 			case 1:
	// 				IconComponent = (
	// 					<Tooltip title={'Processing...'}>
	// 						<SyncOutlined
	// 							spin
	// 							style={{
	// 								color: '#172b6f',
	// 							}}
	// 							// className={styles.indicatorIcon}
	// 						/>
	// 					</Tooltip>
	// 				);
	// 				break;

	// 			case 2:
	// 				IconComponent = (
	// 					<Tooltip title={'Analysis complete'}>
	// 						<CheckCircleOutlined
	// 							style={{
	// 								color: '#58C622',
	// 							}}
	// 							// className={styles.indicatorIcon}
	// 						/>
	// 					</Tooltip>
	// 				);
	// 				break;

	// 			case 3:
	// 			case 4:
	// 			case 5:
	// 				IconComponent = (
	// 					<Tooltip title={'We experienced an issue analysing this video.'}>
	// 						<ExclamationCircleOutlined
	// 							style={{
	// 								color: 'orange',
	// 							}}
	// 							// className={styles.indicatorIcon}
	// 						/>
	// 					</Tooltip>
	// 				);
	// 				break;

	// 			case 6:
	// 			case 7:
	// 				IconComponent = (
	// 					<Tooltip
	// 						title={
	// 							record?.status === 6
	// 								? 'This video exceeds the maximum length (45 mins)'
	// 								: 'This video is too short for meaningful analysis'
	// 						}
	// 					>
	// 						<ExclamationCircleOutlined
	// 							style={{
	// 								color: 'darkred',
	// 							}}
	// 						/>
	// 					</Tooltip>
	// 				);
	// 				break;

	// 			default:
	// 				IconComponent = (
	// 					<Tooltip title={'Adding to queue...'}>
	// 						<LoadingOutlined
	// 							style={{ color: 'gray' }}
	// 							// className={styles.indicatorIcon}
	// 						/>
	// 					</Tooltip>
	// 				);
	// 				break;
	// 		}

	// 		return (
	// 			<>
	// 				<Space size={[8, 1]}>
	// 					{/* <Avatar
	// 					src={
	// 						record?.thumbnail
	// 							? prepareImageSrc(record?.thumbnail)
	// 							: placeholderImage
	// 					}
	// 					alt={`${record?.platform} avatar`}
	// 				/>{' '} */}
	// 					{IconComponent}
	// 					<ThumbnailImage
	// 						src={
	// 							record?.thumbnail
	// 								? prepareImageSrc(record.thumbnail)
	// 								: undefined
	// 						}
	// 						placeholderImage={placeholderImage}
	// 					/>
	// 					<Link href={record?.url} ellipsis target="_blank">
	// 						{preparedTitle || record?.url}
	// 					</Link>
	// 				</Space>
	// 				{record?.favourite && <FavouriteIcon />}
	// 			</>
	// 		);
	// 	},
	// },
	// {
	// 	title: 'Platform / Creator',
	// 	dataIndex: 'platform',
	// 	key: 'platform',
	// 	// sorter: true,
	// 	elipsis: true,
	// 	width: 60,
	// 	// flex: true,
	// 	// hidden: true,
	// 	render: (_: any, record: CopilotHistoryBatchItem) => {
	// 		return (
	// 			<Flex justify="flex-start" align="center" gap={8}>
	// 				<Platforms platform={record?.platform} />

	// 				{record?.creatorUrls?.[0] ? (
	// 					<Button
	// 						type="link"
	// 						href={record?.creatorUrls?.[0]}
	// 						target="_blank"
	// 						style={{
	// 							width: 'fit-content',
	// 							display: 'flex',
	// 							wordWrap: 'break-word',
	// 							overflow: 'hidden',
	// 							padding: 0,
	// 						}}
	// 					>
	// 						<span
	// 							style={{
	// 								width: '100%',
	// 								display: 'inline-block',
	// 								overflow: 'hidden',
	// 								textOverflow: 'ellipsis',
	// 							}}
	// 						>
	// 							{record?.creatorName}
	// 						</span>
	// 					</Button>
	// 				) : (
	// 					<>
	// 						{record?.creatorName ? (
	// 							<Typography.Text
	// 								style={{
	// 									width: 'auto',
	// 									display: 'inline-block',
	// 									overflow: 'hidden',
	// 									textOverflow: 'ellipsis',
	// 								}}
	// 							>
	// 								{record?.creatorName}
	// 							</Typography.Text>
	// 						) : null}
	// 					</>
	// 				)}
	// 			</Flex>
	// 		);
	// 	},
	// },
];

export default columns;
